import React from 'react';
import { motion } from 'framer-motion';

const AICharactersIllustration = () => {
  return (
    <div className="relative w-full flex items-center justify-center py-4">
      <svg 
        viewBox="0 0 700 280" 
        className="w-full h-auto"
        style={{ minHeight: '350px', maxHeight: '450px' }}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background dots */}
        <circle cx="30" cy="200" r="3" fill="#94a3b8" opacity="0.3" />
        <circle cx="670" cy="180" r="3" fill="#94a3b8" opacity="0.3" />
        <circle cx="350" cy="260" r="2" fill="#94a3b8" opacity="0.3" />
        
        {/* ========== CHARACTER 1 - Searching with magnifying glass ========== */}
        <g transform="translate(-20, 0)">
          {/* Shadow */}
          <ellipse cx="100" cy="245" rx="30" ry="8" fill="#94a3b8" opacity="0.15" />
          
          {/* Body */}
          <path 
            d="M75 150 C75 150, 70 180, 75 200 C80 220, 120 220, 125 200 C130 180, 125 150, 125 150" 
            stroke="#94a3b8" 
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Body fill hint */}
          <path 
            d="M80 160 C80 160, 78 185, 82 200 C86 212, 114 212, 118 200 C122 185, 120 160, 120 160" 
            fill="#0f172a"
            opacity="0.3"
          />
          
          {/* Head */}
          <circle cx="100" cy="120" r="32" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Visor shine */}
          <path d="M82 110 Q88 100, 98 105" stroke="#94a3b8" strokeWidth="1.5" fill="none" opacity="0.4" />
          
          {/* Eyes */}
          <circle cx="90" cy="120" r="4" fill="#94a3b8" />
          <circle cx="110" cy="120" r="4" fill="#94a3b8" />
          
          {/* Antenna */}
          <line x1="100" y1="88" x2="100" y2="70" stroke="#94a3b8" strokeWidth="2" />
          <motion.circle 
            cx="100" cy="65" r="5" 
            fill="#14b8a6"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          {/* Left arm holding magnifying glass */}
          <path d="M75 160 Q55 155, 45 140" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Magnifying glass */}
          <circle cx="35" cy="125" r="18" stroke="#94a3b8" strokeWidth="2" fill="none" />
          <line x1="48" y1="138" x2="60" y2="150" stroke="#94a3b8" strokeWidth="3" strokeLinecap="round" />
          
          {/* Right arm */}
          <path d="M125 165 Q140 170, 145 185" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Legs */}
          <path d="M85 218 Q80 235, 78 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M115 218 Q120 235, 122 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Chest square */}
          <rect x="90" y="170" width="20" height="15" rx="3" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
        </g>
        
        {/* Floating icon - Brain/AI */}
        <motion.g
          animate={{ y: [-3, 3, -3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="55" cy="60" r="16" fill="#14b8a6" />
          <path d="M48 60 Q55 52, 62 60 Q55 68, 48 60" stroke="white" strokeWidth="1.5" fill="none" />
          <line x1="51" y1="56" x2="59" y2="56" stroke="white" strokeWidth="1" />
          <line x1="51" y1="64" x2="59" y2="64" stroke="white" strokeWidth="1" />
        </motion.g>
        
        {/* Floating icon - Code */}
        <motion.g
          animate={{ y: [3, -3, 3] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        >
          <circle cx="160" cy="80" r="14" fill="#f59e0b" />
          <path d="M154 80 L157 76 L154 80 L157 84" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M166 80 L163 76 L166 80 L163 84" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </motion.g>
        
        {/* Sparkles near character 1 */}
        <motion.g
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <path d="M140 50 L142 45 L144 50 L149 52 L144 54 L142 59 L140 54 L135 52 Z" fill="#14b8a6" />
        </motion.g>
        
        {/* Dotted line decoration */}
        <path d="M130 240 Q180 225, 230 240" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="4,4" opacity="0.3" />
        
        
        {/* ========== CHARACTER 2 - Waving/Greeting ========== */}
        <g transform="translate(-70, 0)">
          {/* Shadow */}
          <ellipse cx="300" cy="245" rx="28" ry="7" fill="#94a3b8" opacity="0.15" />
          
          {/* Body */}
          <path 
            d="M275 155 C275 155, 272 182, 277 202 C282 218, 318 218, 323 202 C328 182, 325 155, 325 155" 
            stroke="#94a3b8" 
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Body fill */}
          <path 
            d="M280 162 C280 162, 278 185, 282 200 C286 212, 314 212, 318 200 C322 185, 320 162, 320 162" 
            fill="#0f172a"
            opacity="0.3"
          />
          
          {/* Head */}
          <circle cx="300" cy="125" r="30" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Visor shine */}
          <path d="M284 115 Q290 107, 298 112" stroke="#94a3b8" strokeWidth="1.5" fill="none" opacity="0.4" />
          
          {/* Eyes - happy */}
          <circle cx="290" cy="125" r="4" fill="#94a3b8" />
          <circle cx="310" cy="125" r="4" fill="#94a3b8" />
          
          {/* Antenna */}
          <line x1="300" y1="95" x2="300" y2="78" stroke="#94a3b8" strokeWidth="2" />
          <motion.circle 
            cx="300" cy="73" r="5" 
            fill="#10b981"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.8, repeat: Infinity }}
          />
          
          {/* Left arm - waving up */}
          <path d="M275 165 Q250 145, 245 115" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          {/* Hand */}
          <circle cx="243" cy="110" r="8" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Right arm */}
          <path d="M325 170 Q345 180, 350 200" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Legs */}
          <path d="M287 216 Q282 232, 280 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M313 216 Q318 232, 320 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Chest detail */}
          <circle cx="300" cy="178" r="6" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
          <circle cx="300" cy="178" r="2" fill="#94a3b8" />
        </g>
        
        {/* Floating icon - Chat/Speech bubble */}
        <motion.g
          animate={{ y: [-4, 4, -4] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="260" cy="55" r="15" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          <ellipse cx="260" cy="53" rx="7" ry="5" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
          <path d="M256 58 L254 64 L260 59" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
        </motion.g>
        
        {/* Floating icon - Eye/Vision */}
        <motion.g
          animate={{ y: [2, -4, 2] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
        >
          <circle cx="355" cy="70" r="13" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          <ellipse cx="355" cy="70" rx="7" ry="5" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
          <circle cx="355" cy="70" r="2" fill="#94a3b8" />
        </motion.g>
        
        {/* Sparkles */}
        <motion.g
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
        >
          <path d="M230 90 L232 85 L234 90 L239 92 L234 94 L232 99 L230 94 L225 92 Z" fill="#94a3b8" opacity="0.6" />
        </motion.g>
        
        <motion.g
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.8 }}
        >
          <path d="M340 95 L341 91 L342 95 L346 96 L342 97 L341 101 L340 97 L336 96 Z" fill="#94a3b8" opacity="0.6" />
        </motion.g>
        
        
        {/* ========== CHARACTER 3 - Jumping/Excited (arms up) ========== */}
        <g transform="translate(-120, 0)">
          {/* Shadow - smaller because jumping */}
          <ellipse cx="500" cy="250" rx="22" ry="5" fill="#94a3b8" opacity="0.1" />
          
          {/* Body */}
          <path 
            d="M478 145 C478 145, 475 170, 480 188 C485 202, 515 202, 520 188 C525 170, 522 145, 522 145" 
            stroke="#94a3b8" 
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Body fill */}
          <path 
            d="M482 150 C482 150, 480 172, 484 186 C488 196, 512 196, 516 186 C520 172, 518 150, 518 150" 
            fill="#0f172a"
            opacity="0.3"
          />
          
          {/* Head */}
          <circle cx="500" cy="115" r="30" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Happy face */}
          <circle cx="490" cy="112" r="4" fill="#94a3b8" />
          <circle cx="510" cy="112" r="4" fill="#94a3b8" />
          {/* Smile */}
          <path d="M490 125 Q500 133, 510 125" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Antenna */}
          <line x1="500" y1="85" x2="500" y2="65" stroke="#94a3b8" strokeWidth="2" />
          <motion.circle 
            cx="500" cy="60" r="6" 
            fill="#14b8a6"
            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          
          {/* Arms up - celebrating */}
          <path d="M478 155 Q455 135, 450 105" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M522 155 Q545 135, 550 105" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Hands */}
          <circle cx="448" cy="100" r="7" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          <circle cx="552" cy="100" r="7" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Legs - spread (jumping) */}
          <path d="M488 200 Q478 220, 470 240" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M512 200 Q522 220, 530 240" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Backpack */}
          <rect x="522" y="155" width="12" height="28" rx="4" stroke="#94a3b8" strokeWidth="1.5" fill="#0f172a" />
        </g>
        
        {/* Floating icon - Dropbox style (represents cloud/storage) */}
        <motion.g
          animate={{ y: [-5, 5, -5] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="510" cy="30" r="16" fill="#3b82f6" />
          <path d="M503 30 L510 24 L517 30 L510 36 Z" fill="white" />
          <path d="M503 30 L510 36 L510 42 L503 36 Z" fill="white" opacity="0.7" />
          <path d="M517 30 L510 36 L510 42 L517 36 Z" fill="white" opacity="0.7" />
        </motion.g>
        
        {/* Floating icon - Connection/Network */}
        <motion.g
          animate={{ y: [3, -5, 3] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
        >
          <circle cx="565" cy="55" r="12" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          <circle cx="562" cy="52" r="2" fill="#94a3b8" />
          <circle cx="570" cy="52" r="2" fill="#94a3b8" />
          <circle cx="566" cy="60" r="2" fill="#94a3b8" />
          <line x1="562" y1="52" x2="566" y2="60" stroke="#94a3b8" strokeWidth="1" />
          <line x1="570" y1="52" x2="566" y2="60" stroke="#94a3b8" strokeWidth="1" />
        </motion.g>
        
        {/* Heart bubble */}
        <motion.g
          animate={{ y: [-3, 3, -3], scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <circle cx="440" cy="50" r="12" stroke="#ec4899" strokeWidth="2" fill="#0f172a" />
          <path d="M440 56 L435 51 Q435 46, 440 49 Q445 46, 445 51 Z" fill="#ec4899" />
        </motion.g>
        
        {/* Sparkles around jumping character */}
        <motion.g
          animate={{ opacity: [0, 1, 0], y: [-2, -8, -2] }}
          transition={{ duration: 1.2, repeat: Infinity }}
        >
          <circle cx="460" cy="80" r="2" fill="#14b8a6" />
        </motion.g>
        <motion.g
          animate={{ opacity: [0, 1, 0], y: [-2, -8, -2] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: 0.4 }}
        >
          <circle cx="540" cy="85" r="2" fill="#10b981" />
        </motion.g>
        
        
        {/* ========== CHARACTER 4 - Holding tablet/reading ========== */}
        <g transform="translate(-170, 0)">
          {/* Ground hill */}
          <path d="M650 248 Q700 235, 750 248" stroke="#94a3b8" strokeWidth="1.5" fill="none" opacity="0.4" />
          
          {/* Shadow */}
          <ellipse cx="700" cy="245" rx="30" ry="7" fill="#94a3b8" opacity="0.15" />
          
          {/* Body */}
          <path 
            d="M675 148 C675 148, 672 175, 677 195 C682 212, 718 212, 723 195 C728 175, 725 148, 725 148" 
            stroke="#94a3b8" 
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
          
          {/* Body fill */}
          <path 
            d="M680 155 C680 155, 678 178, 682 193 C686 205, 714 205, 718 193 C722 178, 720 155, 720 155" 
            fill="#0f172a"
            opacity="0.3"
          />
          
          {/* Head */}
          <circle cx="700" cy="118" r="30" stroke="#94a3b8" strokeWidth="2" fill="#0f172a" />
          
          {/* Eyes - looking at tablet */}
          <circle cx="692" cy="118" r="3" fill="#94a3b8" />
          <circle cx="708" cy="118" r="3" fill="#94a3b8" />
          
          {/* Mouth - focused */}
          <ellipse cx="700" cy="130" rx="4" ry="2" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
          
          {/* Antenna */}
          <line x1="700" y1="88" x2="700" y2="70" stroke="#94a3b8" strokeWidth="2" />
          <motion.circle 
            cx="700" cy="65" r="5" 
            fill="#f59e0b"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.2, repeat: Infinity }}
          />
          
          {/* Arms holding tablet */}
          <path d="M675 160 Q655 165, 645 180" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M725 160 Q745 165, 755 180" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Tablet */}
          <rect x="640" y="175" width="40" height="50" rx="4" fill="#f59e0b" stroke="#f59e0b" strokeWidth="2" />
          <rect x="645" y="180" width="30" height="38" rx="2" fill="#0f172a" />
          {/* Screen content */}
          <motion.path 
            d="M650 200 L655 195 L662 198 L670 188"
            stroke="#14b8a6"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <circle cx="752" cy="183" r="4" fill="#f59e0b" />
          
          {/* Legs */}
          <path d="M687 210 Q682 228, 680 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M713 210 Q718 228, 720 245" stroke="#94a3b8" strokeWidth="2" fill="none" strokeLinecap="round" />
          
          {/* Backpack */}
          <rect x="725" y="155" width="12" height="30" rx="4" stroke="#94a3b8" strokeWidth="1.5" fill="#0f172a" />
        </g>
        
        {/* Floating icon - Image/Photo (like the purple bear in reference) */}
        <motion.g
          animate={{ y: [-4, 4, -4] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        >
          <rect x="420" y="170" width="28" height="28" rx="4" fill="#8b5cf6" />
          <circle cx="430" cy="180" r="4" fill="white" opacity="0.8" />
          <path d="M424 192 L434 184 L440 190 L444 186 L444 194 L424 194 Z" fill="white" opacity="0.6" />
        </motion.g>
        
        {/* Floating icon - Compass/Safari style */}
        <motion.g
          animate={{ y: [3, -3, 3], rotate: [0, 5, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
        >
          <circle cx="610" cy="80" r="15" fill="#0f172a" stroke="#f97316" strokeWidth="2" />
          <circle cx="610" cy="80" r="4" stroke="#f97316" strokeWidth="1.5" fill="none" />
          <line x1="610" y1="68" x2="610" y2="72" stroke="#f97316" strokeWidth="2" />
          <line x1="610" y1="88" x2="610" y2="92" stroke="#f97316" strokeWidth="2" />
          <line x1="598" y1="80" x2="602" y2="80" stroke="#f97316" strokeWidth="2" />
          <line x1="618" y1="80" x2="622" y2="80" stroke="#f97316" strokeWidth="2" />
        </motion.g>
        
        {/* Final sparkles */}
        <motion.g
          animate={{ opacity: [0.3, 1, 0.3], scale: [0.9, 1.1, 0.9] }}
          transition={{ duration: 2, repeat: Infinity, delay: 1 }}
        >
          <path d="M590 130 L592 125 L594 130 L599 132 L594 134 L592 139 L590 134 L585 132 Z" fill="#f59e0b" />
        </motion.g>
        
        {/* Connecting dotted path */}
        <path 
          d="M90 242 Q160 225, 210 242 Q280 258, 320 242 Q380 225, 430 242 Q500 258, 530 242" 
          stroke="#94a3b8" 
          strokeWidth="1.5" 
          strokeDasharray="4,4"
          fill="none"
          opacity="0.25"
        />
        
        {/* Small dots decoration */}
        <circle cx="150" cy="235" r="2" fill="#94a3b8" opacity="0.4" />
        <circle cx="300" cy="248" r="2" fill="#94a3b8" opacity="0.4" />
        <circle cx="460" cy="235" r="2" fill="#94a3b8" opacity="0.4" />
        
      </svg>
    </div>
  );
};

export default AICharactersIllustration;